import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
} from "react";

import { cx } from "@/vnext/shared/cx";
import { useDrawerOpen } from "@/vnext/shared/DrawerOpenContext";
import { lockPageScroll } from "@/vnext/shared/pageScrollLock";
import { useRecoveryNotices } from "@/vnext/shared/RecoveryNoticesContext";
import "./VNextDialog.css";

interface VNextDialogProps {
  open: boolean;
  title: string;
  tone?: "default" | "destructive";
  description?: string;
  children: ReactNode;
  backdropClassName?: string;
  className?: string;
  footer?: ReactNode;
  onClose: () => void;
  allowKeyDownPropagation?: (
    event: ReactKeyboardEvent<HTMLDivElement>,
  ) => boolean;
  returnFocusRef?: RefObject<HTMLElement | null>;
  size?: "default" | "wide" | "fullscreen";
  layout?: "modal" | "sheet";
  modal?: boolean;
}

const focusableDialogSelector = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

const getDialogFocusableElements = (dialogElement: HTMLElement) =>
  Array.from(
    dialogElement.querySelectorAll<HTMLElement>(focusableDialogSelector),
  ).filter(
    (element) =>
      !element.hasAttribute("disabled") &&
      element.getAttribute("aria-hidden") !== "true",
  );

export const VNextDialog = ({
  open,
  title,
  tone = "default",
  description,
  children,
  backdropClassName,
  className,
  footer,
  onClose,
  allowKeyDownPropagation,
  returnFocusRef,
  size = "default",
  layout = "modal",
  modal = true,
}: VNextDialogProps) => {
  const titleId = useId();
  const descriptionId = useId();
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const recoveryNotices = useRecoveryNotices();
  useDrawerOpen(open && layout === "sheet");
  useEffect(
    () => (open && modal ? lockPageScroll() : undefined),
    [open, modal],
  );

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previouslyFocused =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const returnFocus = returnFocusRef?.current;
    dialogRef.current?.focus({ preventScroll: true });

    return () => {
      const target =
        returnFocus &&
        document.contains(returnFocus) &&
        !returnFocus.matches(":disabled")
          ? returnFocus
          : previouslyFocused;
      if (target && document.contains(target)) {
        target.focus({ preventScroll: true });
      }
    };
  }, [open, returnFocusRef]);

  const wasModal = useRef(modal);
  useEffect(() => {
    const becameModal = !wasModal.current && modal;
    wasModal.current = modal;
    if (
      !open ||
      !becameModal ||
      dialogRef.current?.contains(document.activeElement)
    ) {
      return;
    }
    const dialogs = document.querySelectorAll(
      "[role='dialog'][aria-modal='true']",
    );
    if (dialogs[dialogs.length - 1] === dialogRef.current) {
      dialogRef.current.focus({ preventScroll: true });
    }
  }, [modal, open]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    // A disabled focused control can drop focus to the body. Leave Escape on
    // focused page controls to those controls; only the body needs a fallback.
    const onDocumentKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Escape" ||
        event.defaultPrevented ||
        document.activeElement !== document.body
      ) {
        return;
      }
      // Any modal on top (e.g. a Tagging Panel opened from a sheet) owns
      // the key, so only the topmost modal of any kind may close.
      const dialogs = document.querySelectorAll(
        "[role='dialog'][aria-modal='true']",
      );
      if (
        modal
          ? dialogs[dialogs.length - 1] === dialogRef.current
          : dialogs.length === 0
      ) {
        // Mark it handled so shell panels listening on window stay open.
        event.preventDefault();
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onDocumentKeyDown);
    return () => document.removeEventListener("keydown", onDocumentKeyDown);
  }, [modal, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className={cx(
        "vnext-dialog-backdrop",
        layout === "sheet" && "vnext-dialog-backdrop--sheet",
        backdropClassName,
      )}
      onClick={modal ? onClose : undefined}
      onKeyDown={(event) => {
        // Only Escape should dismiss the backdrop. Enter/Space on the
        // focused surface must remain available to the dialog content.
        if (modal && event.key === "Escape") {
          onClose();
        }
      }}
      role="presentation"
    >
      <div
        aria-describedby={description ? descriptionId : undefined}
        aria-labelledby={titleId}
        aria-modal={modal ? "true" : undefined}
        className={cx(
          "vnext-dialog",
          tone === "destructive" && "vnext-dialog--destructive",
          size === "wide" && "vnext-dialog--wide",
          size === "fullscreen" && "vnext-dialog--fullscreen",
          layout === "sheet" && "vnext-dialog--sheet",
          className,
        )}
        ref={dialogRef}
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
          if (modal && event.key === "Tab") {
            const focusableElements = getDialogFocusableElements(
              event.currentTarget,
            );
            if (focusableElements.length === 0) {
              event.preventDefault();
              event.currentTarget.focus();
            } else {
              const firstElement = focusableElements[0];
              const lastElement =
                focusableElements[focusableElements.length - 1];
              const activeElement = document.activeElement;
              const focusIsOnDialog = activeElement === event.currentTarget;

              if (
                event.shiftKey &&
                (focusIsOnDialog ||
                  activeElement === firstElement ||
                  !event.currentTarget.contains(activeElement))
              ) {
                event.preventDefault();
                lastElement.focus();
              } else if (
                !event.shiftKey &&
                (focusIsOnDialog || activeElement === lastElement)
              ) {
                event.preventDefault();
                firstElement.focus();
              }
            }
          }
          // Escape and a modal's Tab belong to this dialog even when a caller
          // admits a document-level shortcut for its own embedded editor.
          if (
            event.key === "Escape" ||
            (modal && event.key === "Tab") ||
            (event.key !== "Tab" && !allowKeyDownPropagation?.(event))
          ) {
            event.stopPropagation();
          }
        }}
        role="dialog"
        tabIndex={-1}
      >
        <header className="vnext-dialog__header">
          <div>
            <h2 className="vnext-dialog__title" id={titleId}>
              {title}
            </h2>
            {description ? (
              <p className="vnext-dialog__description" id={descriptionId}>
                {description}
              </p>
            ) : null}
          </div>
          <button
            aria-label="Close dialog"
            className="vnext-dialog__close"
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </header>
        <div
          className={cx(
            "vnext-dialog__body",
            recoveryNotices.length > 0 && "vnext-dialog__body--with-recovery",
          )}
        >
          {recoveryNotices.length > 0 ? (
            <div className="vnext-dialog__recovery-notices">
              {recoveryNotices.map(({ id, content }) => (
                <div key={id}>{content}</div>
              ))}
            </div>
          ) : null}
          {children}
        </div>
        {footer ? (
          <footer className="vnext-dialog__footer">{footer}</footer>
        ) : null}
      </div>
    </div>
  );
};
